<template>
    <div class="is-width-fill">
        <PageHead title="Self Fulfillment" />

        <div class="box">
            <div class="box-title">
                Self Fulfillment
            </div>

            <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <div>
                    <Field
                        v-if="props.fulfillmentOrders.length > 0"
                        is="checkbox"
                        v-model="allSelected"
                        class="mb-0 ml-2 is-large"
                    />
                </div>

                <div class="is-flex is-align-items-center is-gap-2">
                    <Field
                        is="select"
                        v-model="filter"
                        class="mb-0"
                        @update:model-value="handleFilterChange($event as Filters)"
                    >
                        <option value="pendingPostage">Pending Postage</option>
                        <option value="pendingShipment">Pending Shipment</option>
                        <option value="shipped">Shipped</option>
                    </Field>

                    <button
                        v-if="filter === 'pendingPostage'"
                        class="button is-small is-warning"
                        :class="{'isLoading': isLoading}"
                        :disabled="selected.size === 0"
                        @click.prevent="showBulkEditModal = true"
                    >
                        Bulk Edit
                    </button>

                    <button
                        v-if="filter === 'pendingShipment'"
                        class="button is-small is-warning"
                        :class="{'isLoading': isLoading}"
                        :disabled="selected.size === 0"
                        @click.prevent="handleMarkShipped"
                    >
                        Mark Shipped
                    </button>
                </div>
            </div>

            <template v-for="order in props.fulfillmentOrders">
                <FulfillmentOrderItem
                    :selected="selected.has(order.id)"
                    @update:selected="$event ? selected.add(order.id) : selected.delete(order.id)"
                    :fulfillment-order="order"
                />
            </template>

            <AppPagination
                v-if="props.fulfillmentOrders.length !== 0"
                :current-page="pageInfo.currentPage"
                :last-page="pageInfo.lastPage"
                :total="pageInfo.total"
                :from="pageInfo.from"
                :to="pageInfo.to"
                @update:page="handleFilterChange(undefined, $event)"
            />

            <template v-if="props.fulfillmentOrders.length === 0">
                <p class="has-text-grey-light">
                    There are no orders matching the selected filters.
                </p>
            </template>
        </div>

        <FulfillmentOrderBulkEditModal
            :open="showBulkEditModal"
            :selected-ids="Array.from(selected)"
            @close="showBulkEditModal = false"
        />
    </div>
</template>

<script setup lang="ts">
import Base from "@/Layouts/Base.vue";
import Default from "@/Layouts/Default.vue";
import PageHead from "@/Components/PageHead.vue";
import {FulfillmentOrder} from "@/Types/FulfillmentOrder";
import FulfillmentOrderItem from "@/Components/FulfillmentOrderItem.vue";
import {computed, onBeforeMount, onBeforeUnmount, onMounted, ref} from "vue";
import FulfillmentOrderBulkEditModal from "@/Components/FulfillmentOrderBulkEditModal.vue";
import Field from "@/Components/common/Field.vue";
import {router} from "@inertiajs/vue3";
import {PageInfo} from "@/Types/PageInfo";
import AppPagination from "@/Components/common/AppPagination.vue";

defineOptions({
    layout: [Base, Default]
});

const props = defineProps<{
    fulfillmentOrders: FulfillmentOrder[],
    pageInfo: PageInfo,
}>();

type Filters = 'pendingPostage' | 'pendingShipment' | 'shipped';

const selected = ref<Set<number>>(new Set());
const isLoading = ref(false);
const showBulkEditModal = ref(false);
const filter = ref<Filters>();
const page = ref(1);

const removeNavigationEvent = ref(null);

onBeforeMount(() => {
    const params = new URLSearchParams(window.location.search);
    filter.value = (params.get('filter') ?? 'pendingPostage') as Filters;
    page.value = Number(params.get('page')) || 1;
});

onMounted(() => {
    removeNavigationEvent.value = router.on('finish', () => {
        selected.value = new Set();
    });
});

onBeforeUnmount(() => {
    removeNavigationEvent.value?.();
});

function handleFilterChange(newFilter?: Filters, newPage?: number) {
    if (!newPage) {
        page.value = 1;
    }

    router.get(route('admin.self_fulfillment.index', {
        filter: newFilter ?? filter.value,
        page: newPage ?? 1,
    }), {}, {
        replace: true,
    });
}

const allSelected = computed({
    get: () => {
        return props.fulfillmentOrders.length === selected.value.size;
    },
    set: (newVal: boolean) => {
        if (newVal) {
            for (const order of props.fulfillmentOrders) {
                selected.value.add(order.id);
            }
        } else {
            selected.value = new Set();
        }
    }
});

function handleMarkShipped() {
    if (selected.value.size === 0) {
        return;
    }

    router.post(route('admin.self_fulfillment.mark_shipped'), {
        orderIds: Array.from(selected.value)
    }, {
        onStart: () => {
            isLoading.value = true;
        },
        onFinish: () => {
            isLoading.value = false;
            selected.value = new Set();
        }
    });
}
</script>
